//src/hooks/useResizableColumns.ts
import { useCallback, useMemo, useState } from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import ResizableTitle from '../utils/ResizableTitle';

type WidthMap = Record<string, number>;

type ResCol<T> = (ColumnsType<T>[number] & {
  key: React.Key;
  width?: number;
  resizable?: boolean;
  fixed?: 'left' | 'right' | boolean;
});

type PersistShape = {
  widths?: WidthMap;
  order?: string[];
  hidden?: string[];
};

function canUseDOM() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function load(storageKey: string): PersistShape {
  if (!canUseDOM()) return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function save(storageKey: string, data: PersistShape) {
  if (!canUseDOM()) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(data));
  } catch {}
}

export function useResizableColumns<T>(
  baseColumns: ResCol<T>[],
  opts: {
    storageKey: string;
    enableVisibility?: boolean;
    enableReorder?: boolean;
  }
) {
  const { storageKey, enableVisibility = true, enableReorder = true } = opts;

  const baseKeys = useMemo(() => baseColumns.map(c => String(c.key)), [baseColumns]);
  const baseMap = useMemo(() => {
    const m = new Map<string, ResCol<T>>();
    baseColumns.forEach(c => m.set(String(c.key), c));
    return m;
  }, [baseColumns]);

  const persisted = useMemo(() => load(storageKey), [storageKey]);

  const [order, setOrder] = useState<string[]>(
    () => (persisted.order && persisted.order.every(k => baseMap.has(k))
      ? persisted.order
      : baseKeys)
  );
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set((persisted.hidden ?? []).filter(k => baseMap.has(k)))
  );
  const [widths, setWidths] = useState<WidthMap>(() => ({ ...(persisted.widths ?? {}) }));

  const persistAll = useCallback((next?: Partial<PersistShape>) => {
    const data: PersistShape = {
      widths,
      order,
      hidden: Array.from(hidden),
      ...next,
    };
    save(storageKey, data);
  }, [storageKey, widths, order, hidden]);

  // ширины
  const setColWidth = useCallback((key: string, w: number) => {
    setWidths(prev => {
      const next = { ...prev, [key]: w };
      persistAll({ widths: next });
      return next;
    });
  }, [persistAll]);

  // явное управление видимостью (чтобы корректно работать с Menu selectable)
  const setVisibility = useCallback((key: string, visible: boolean) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (visible) next.delete(key);
      else next.add(key);
      // не даём скрыть все колонки
      const visibleCount = baseKeys.filter(k => !next.has(k)).length;
      if (visibleCount === 0) next.delete(key);
      persistAll({ hidden: Array.from(next) });
      return next;
    });
  }, [persistAll, baseKeys]);

  // DnD
  const [dragKey, setDragKey] = useState<string | null>(null);
  const onDragStart = useCallback((key: string) => setDragKey(key), []);
  const onDragEnd = useCallback(() => setDragKey(null), []);
  const onDropTo = useCallback((targetKey: string, pos: 'left' | 'right') => {
    if (!dragKey || dragKey === targetKey) return;
    setOrder(prev => {
      const arr = prev.filter(k => k !== dragKey);
      const idx = arr.indexOf(targetKey);
      const insertAt = pos === 'left' ? idx : idx + 1;
      arr.splice(insertAt, 0, dragKey);
      persistAll({ order: arr });
      return arr;
    });
    setDragKey(null);
  }, [dragKey, persistAll]);

  // построение списка колонок с учётом порядка, видимости и ширин
  const arrangedCols = useMemo(() => {
    const keys = order.filter(k => baseMap.has(k));
    const list = keys.map(k => {
      const c = { ...(baseMap.get(k) as ResCol<T>) };
      const w = widths[k];
      if (typeof w === 'number') c.width = w;
      return c;
    });
    return list.filter(c => !hidden.has(String(c.key)));
  }, [order, baseMap, widths, hidden]);

  // меню видимости (Dropdown.menu)
  const visibleKeys = useMemo(
    () => baseKeys.filter(k => !hidden.has(k)),
    [baseKeys, hidden]
  );
  const headerMenu = useMemo<MenuProps | undefined>(() => {
    if (!enableVisibility) return undefined;
    return {
      items: baseKeys.map(k => {
        const col = baseMap.get(k)!;
        return { key: k, label: (col.title as any) ?? String(k) };
      }),
      selectable: true,
      multiple: true,
      selectedKeys: visibleKeys,
      onSelect: ({ key }) => setVisibility(String(key), true),
      onDeselect: ({ key }) => setVisibility(String(key), false),
    };
  }, [enableVisibility, baseKeys, baseMap, visibleKeys, setVisibility]);

  // соберём финальные колонки
  const columns = useMemo(
    () =>
      arrangedCols.map((col) => ({
        ...col,
        onHeaderCell: (column: ColumnType<T>) => {
          const k = String((column as any).key);
          return {
            width: column.width,
            resizable: (column as any).resizable !== false,

            // ресайз
            onResize: (_e: any, { size }: { size: { width: number; height: number } }) => {
              setColWidth(k, Math.max(1, size.width));
            },
            onAutoFit: (w: number) => setColWidth(k, Math.max(1, w)),

            // DnD
            draggable: enableReorder,
            onDragStart: enableReorder ? (e: React.DragEvent) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(k); } : undefined,
            onDragOver: enableReorder ? (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined,
            onDrop: enableReorder ? (e: React.DragEvent) => {
              e.preventDefault();
              const th = e.currentTarget as HTMLTableCellElement;
              const rect = th.getBoundingClientRect();
              const pos = (e.clientX - rect.left) < rect.width / 2 ? 'left' : 'right';
              onDropTo(k, pos);
            } : undefined,
            onDragEnd: enableReorder ? onDragEnd : undefined,
            isDragging: dragKey === k,

            // контекстное меню
            menu: headerMenu,
          } as any; // пробрасываем extra props в ResizableTitle
        },
      })) as ColumnsType<T>,
    [arrangedCols, setColWidth, enableReorder, onDragStart, onDropTo, onDragEnd, dragKey, headerMenu]
  );

  const components = useMemo(
    () => ({ header: { cell: ResizableTitle } }),
    []
  );

  return {
    columns,
    components,
    state: { order, hidden: Array.from(hidden), widths },
  };
}

export type { ResCol };
